import { createTypes, completeTypes } from 'redux-recompose';
import download from 'downloadjs';
import { dateToString } from '@widergy/web-utils/lib/date';
import { retrieveErrors } from '@widergy/web-utils/lib/message';
import { downloadPDF } from '@widergy/web-utils/lib/browser';
import Polling from '@widergy/polling-handling';
import { AlertHandler } from '@widergy/energy-ui';

import { appConfig } from 'config/customization';
import BillService from 'services/BillService';
import { message } from 'utils/stringUtils';

import { actionCreators as alertActions } from '../alertHandlers';
import { actionCreators as accountActions } from '../accountHandlers';

/* ------------- Bill actions ------------- */
export const actions = createTypes(
  completeTypes(
    [
      'GET_BILLS',
      'GET_LAST_BILL',
      'GET_CURRENT_BILLS',
      'GET_PRINTED_BILL',
      'GET_PRINTED_BILL_PDF',
      'GET_BILL_PROGRESS',
      'GET_CONSUMPTION_HISTORY',
      'GET_PAYMENT_DOCUMENT',
      'GET_LAST_BILL_INFORMATION',
      'GET_MULTIPLE_BILLS',
      'APPEND_BILLS'
    ],
    ['SET_DEFAULT_STATE', 'RESET_LAST_BILL_INFORMATION']
  ),
  '@@BILL'
);

const privateActionCreators = {
  getBillsSuccess: bills => ({
    type: actions.GET_BILLS_SUCCESS,
    payload: bills,
    target: 'bills'
  }),
  getBillsFailure: error => ({
    type: actions.GET_BILLS_FAILURE,
    payload: { error },
    target: 'bills'
  }),

  getLastBillSuccess: lastBill => ({
    type: actions.GET_LAST_BILL_SUCCESS,
    payload: lastBill,
    target: 'lastBill'
  }),

  getLastBillFailure: error => ({
    type: actions.GET_LAST_BILL_FAILURE,
    payload: { error },
    target: 'lastBill'
  }),

  getCurrentBillSuccess: currentBill => ({
    type: actions.GET_CURRENT_BILLS_SUCCESS,
    payload: currentBill,
    target: 'currentBill'
  }),
  getCurrentBillFailure: error => ({
    type: actions.GET_CURRENT_BILLS_FAILURE,
    payload: { error },
    target: 'currentBill'
  }),
  getPrintedBillSuccess: (printedBill, id, targetTo) => ({
    type: actions.GET_PRINTED_BILL_SUCCESS,
    payload: { printedBill, id },
    target: targetTo
  }),
  getPrintedBillPDFFailure: error => ({
    type: actions.GET_PRINTED_BILL_PDF_FAILURE,
    payload: { error },
    target: 'printedBillPDF'
  }),
  getPrintedBillPDFSuccess: (printedBill, id) => ({
    type: actions.GET_PRINTED_BILL_PDF_SUCCESS,
    payload: { printedBill, id },
    target: 'printedBillPDF'
  }),
  getPrintedBillFailure: (error, targetTo) => ({
    type: actions.GET_PRINTED_BILL_FAILURE,
    payload: { error },
    target: targetTo
  }),
  getBillProgressSuccess: bills => ({
    type: actions.GET_BILL_PROGRESS_SUCCESS,
    payload: bills,
    target: 'billProgress'
  }),
  getBillProgressFailure: error => ({
    type: actions.GET_BILL_PROGRESS_FAILURE,
    payload: { error },
    target: 'billProgress'
  }),
  appendBillsSuccess: bills => ({
    type: actions.APPEND_BILLS_SUCCESS,
    payload: bills,
    target: 'bills'
  }),
  appendBillsFailure: error => ({
    type: actions.APPEND_BILLS_FAILURE,
    payload: { error },
    target: 'bills'
  }),
  getPublicPaymentDocumentSuccess: doc => ({
    type: actions.GET_PAYMENT_DOCUMENT_SUCCESS,
    payload: { doc, isPublic: true },
    target: 'paymentDocument'
  }),
  getPublicPaymentDocumentFailure: error => ({
    type: actions.GET_PAYMENT_DOCUMENT_FAILURE,
    payload: error,
    target: 'paymentDocument'
  }),
  getLastBillInformationSuccess: lastBill => ({
    type: actions.GET_LAST_BILL_INFORMATION_SUCCESS,
    payload: lastBill,
    target: 'lastBillInformation'
  }),
  getLastBillInformationFailure: error => ({
    type: actions.GET_LAST_BILL_INFORMATION_FAILURE,
    payload: { error },
    target: 'lastBillInformation'
  }),
  getMultipleBillsSuccess: result => ({
    type: actions.GET_MULTIPLE_BILLS_SUCCESS,
    payload: result,
    target: 'multipleBills'
  }),
  getMultipleBillsFailure: error => ({
    type: actions.GET_MULTIPLE_BILLS_FAILURE,
    payload: { error },
    target: 'multipleBills'
  }),
  getConsumptionHistorySuccess: result => ({
    type: actions.GET_CONSUMPTION_HISTORY_SUCCESS,
    payload: result,
    target: 'consumptionHistory'
  }),
  getConsumptionHistoryFailure: error => ({
    type: actions.GET_CONSUMPTION_HISTORY_FAILURE,
    payload: error,
    target: 'consumptionHistory'
  })
};

const actionCreators = {
  getBills: (pageNumber = 1, pageSize = 20) => async (dispatch, getState) => {
    const { currentAccount } = getState().account;
    if (!currentAccount) return;
    const accountId = currentAccount.id;
    dispatch({ type: actions.GET_BILLS, target: 'bills' });
    dispatch({ type: actions.SET_DEFAULT_STATE, target: 'printedBill' });
    const response = await Polling.handle(BillService.getBills(accountId, pageNumber, pageSize));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getBillsFailure')));
      dispatch(privateActionCreators.getBillsFailure(response.error));
    } else {
      dispatch(privateActionCreators.getBillsSuccess(response.data.bills));
      dispatch(accountActions.billsUpdated());
    }
  },

  getLastBill: accountId => async dispatch => {
    dispatch({ type: actions.GET_LAST_BILL, target: 'lastBill' });
    const response = await Polling.handle(BillService.getLastBill(accountId));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getLastBillFailure')));
      dispatch(privateActionCreators.getLastBillFailure(response.error));
    } else {
      dispatch(privateActionCreators.getLastBillSuccess(response.data));
    }
  },

  getCurrentBill: accountId => async dispatch => {
    dispatch({ type: actions.GET_CURRENT_BILLS, target: 'currentBill' });
    dispatch({ type: actions.SET_DEFAULT_STATE, target: 'printedBill' });
    const response = await Polling.handle(BillService.getCurrentBill(accountId));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getCurrentBillFailure')));
      dispatch(privateActionCreators.getCurrentBillFailure(response.error));
    } else {
      dispatch(privateActionCreators.getCurrentBillSuccess(response.data));
    }
  },
  getPrintedBill: (billId, target) => async (dispatch, getState) => {
    const { currentAccount } = getState().account;

    if (!currentAccount) return;

    const accountId = currentAccount.id;
    dispatch({ type: actions.GET_PRINTED_BILL, target: 'printedBill' });
    if (appConfig.encodedDocumentDownload) AlertHandler.info(message('Bill:loadingBill'));
    const response = await Polling.handle(BillService.getPrintedBill(accountId, billId));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getPrintedBillFailure')));
      dispatch(privateActionCreators.getPrintedBillFailure(response.error, target));
    } else if (response.data.base64) {
      download(
        `data:application/pdf;base64,${response.data.base64}`,
        `Factura_${dateToString(new Date())}.pdf`,
        'application/pdf'
      );
      dispatch(privateActionCreators.getPrintedBillSuccess(response.data, billId, target));
    } else {
      dispatch(privateActionCreators.getPrintedBillSuccess(response.data, billId, target));
    }
  },

  getPrintedBillPDF: billId => async (dispatch, getState) => {
    const { currentAccount } = getState().account;
    if (!currentAccount) return;
    dispatch({ type: actions.GET_PRINTED_BILL_PDF, target: 'printedBillPDF' });
    const clientNumber = currentAccount.client_number;
    const response = await BillService.getPrintedBillPDF(clientNumber, billId);
    if (response.ok) {
      downloadPDF(response.data, `Factura_${dateToString(new Date())}.pdf`, true);
      dispatch(privateActionCreators.getPrintedBillPDFSuccess());
    } else {
      dispatch(privateActionCreators.getPrintedBillPDFFailure(response.error));
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getPrintedBillFailure')));
    }
  },

  getBillProgress: accountId => async dispatch => {
    dispatch({ type: actions.GET_BILL_PROGRESS, target: 'billProgress' });
    const response = await Polling.handle(BillService.getBillProgress(accountId));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getBillProgressFailure')));
      dispatch(privateActionCreators.getBillProgressFailure(response.error));
    } else {
      dispatch(privateActionCreators.getBillProgressSuccess(response.data));
    }
  },
  getConsumptionHistory: (accountId, periods) => async dispatch => {
    dispatch({ type: actions.GET_CONSUMPTION_HISTORY, target: 'consumptionHistory' });
    const response = await Polling.handle(BillService.getConsumptionHistory(accountId, periods));
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getConsumptionHistoryFailure'))
      );
      dispatch(privateActionCreators.getConsumptionHistoryFailure(response.error));
    } else {
      dispatch(privateActionCreators.getConsumptionHistorySuccess(response.data));
    }
  },
  appendBills: (pageNumber, pageSize) => async (dispatch, getState) => {
    dispatch({ type: actions.APPEND_BILLS });
    const { currentAccount } = getState().account;
    if (!currentAccount) return;
    const accountId = currentAccount.id;
    const response = await Polling.handle(BillService.getBills(accountId, pageNumber, pageSize));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:appendBillsFailure')));
      dispatch(privateActionCreators.appendBillsFailure(response.error));
    } else {
      dispatch(privateActionCreators.appendBillsSuccess(response.data.bills));
    }
  },
  getPublicPaymentDocument: (clientNumber, userData, externalId = null) => async dispatch => {
    dispatch({ type: actions.GET_PAYMENT_DOCUMENT, target: 'paymentDocument' });
    const response = await Polling.handle(
      BillService.getPublicPaymentDocument(clientNumber, userData, externalId)
    );
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getPublicPaymentDocumentFailure'))
      );
      dispatch(privateActionCreators.getPublicPaymentDocumentFailure(response.error));
    } else if (response.data.base64) {
      download(
        `data:application/pdf;base64,${response.data.base64}`,
        `Cupon_pago_${dateToString(new Date())}.pdf`,
        'application/pdf'
      );
      dispatch(privateActionCreators.getPublicPaymentDocumentSuccess(response.data));
    } else {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getPublicPaymentDocumentFailure'))
      );
      dispatch(privateActionCreators.getPublicPaymentDocumentFailure(response.data));
    }
  },
  getLastBillInformation: (clientNumber, email) => async (dispatch, getState) => {
    dispatch({ type: actions.GET_LAST_BILL_INFORMATION, target: 'lastBillInformation' });
    const response = await Polling.handle(BillService.getLastBillInformation(clientNumber, email));
    if (response.error) {
      dispatch(
        alertActions.openModal({
          content: retrieveErrors(response.error, message('DefaultMessages:getLastBillInformationFailure')),
          title: message('PublicLastBill:resultModalTitle')
        })
      );
      dispatch(privateActionCreators.getLastBillInformationFailure(response.error));
    } else {
      dispatch(privateActionCreators.getLastBillInformationSuccess(response.data));
      dispatch(
        alertActions.openModal({
          content: getState().bill.lastBillInformation.message,
          title: message('PublicLastBill:resultModalTitle')
        })
      );
    }
  },
  resetLastBillInformation: () => ({
    type: actions.RESET_LAST_BILL_INFORMATION
  }),
  getMultipleBills: account => async dispatch => {
    if (!account) return;
    dispatch({ type: actions.GET_MULTIPLE_BILLS, target: 'multipleBills' });
    const response = await Polling.handle(BillService.getMultipleBills(account.client_number));
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getMultipleBillsFailure')));
      dispatch(privateActionCreators.getMultipleBillsFailure(response.error));
    } else {
      dispatch(privateActionCreators.getMultipleBillsSuccess(response.data.url));
    }
  }
};

export default actionCreators;
